import { useSelector } from 'react-redux';

import { RadioGroup } from '@/components/RadioGroup';
import { RevealSlot } from '@/components/RevealSlot';
import {
    EEmploymentStatus,
    EIncomeSource,
    EOtherIncomeSource,
    IEmploymentByIndex,
    NO_INCOME_EXPLANATION,
    selectEmploymentIndex,
    selectEmploymentStatusByIndex,
    selectOtherIncomeSource,
    setEmploymentIndex,
    setEmploymentStatusByIndex,
    setOtherIncomeSource,
} from '@/context/borrower';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';

import { CasualLength } from '../CasualLength';

export interface IEmploymentStatus {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'EmploymentStatus';

/**
 * The employment status a source travels as. Government benefits is a value
 * Manage accepts, so it goes as a status like any other; no regular income
 * has none, so it carries nothing here and goes as a note instead.
 */
const SOURCE_STATUS: Partial<Record<EIncomeSource, EEmploymentStatus>> = {
    [EIncomeSource.GOVERNMENT_PAYMENT]: EEmploymentStatus.GOVERNMENT_BENEFITS,
};

/**
 * What the Manage note says for a source with no home in Manage's employment
 * status list. Sent as `statusOtherExplanation`, which the API already
 * persists — see IEmployment.
 */
const SOURCE_EXPLANATION: Partial<Record<EIncomeSource, string>> = {
    [EIncomeSource.NO_INCOME]: NO_INCOME_EXPLANATION,
};

export function EmploymentStatus({
    baseId,
    showErrors,
}: IEmploymentStatus): JSX.Element {
    // ***** Redux *****
    const employment = useSelector((state: RootState) =>
        selectEmploymentStatusByIndex(state, 0)
    );
    const incomeSource = employment?.incomeSource;
    const employmentStatus = employment?.status;
    const employmentIndex = useAppSelector(selectEmploymentIndex);
    const dispatch = useAppDispatch();
    // A partner on a light ground already has grey behind the journey, so a
    // tinted panel would dissolve into it. There the group is a white card
    // with an edge; on the white journey it is the tint that marks it out.
    const { cardGround } = useCopy();
    const otherIncomeSource = useAppSelector(selectOtherIncomeSource);

    // ***** Event Handlers *****
    function update(patch: Partial<typeof employment>): void {
        const dispatchObject: IEmploymentByIndex = {
            index: 0,
            employment: {
                ...employment,
                ...patch,
            },
        };
        if (employmentIndex !== 0) dispatch(setEmploymentIndex(0));
        dispatch(setEmploymentStatusByIndex(dispatchObject));
    }

    function handleIncomeSourceChange(
        value: string | boolean | undefined
    ): void {
        const nextSource = value as EIncomeSource;
        // Leaving WORK clears the work answers, so a stale kind-of-work or
        // casual length can't travel with an applicant who no longer has a job.
        // Government benefits stops being offered as a source of OTHER income
        // once it is the main one, so a selection made earlier has to go with
        // it — otherwise it is held in state while absent from the screen.
        if (
            nextSource === EIncomeSource.GOVERNMENT_PAYMENT &&
            otherIncomeSource === EOtherIncomeSource.GOVERNMENT_BENEFITS
        ) {
            dispatch(setOtherIncomeSource(undefined));
        }
        if (nextSource !== EIncomeSource.WORK) {
            update({
                incomeSource: nextSource,
                status: SOURCE_STATUS[nextSource],
                statusOtherExplanation: SOURCE_EXPLANATION[nextSource],
                years: undefined,
                months: undefined,
            });
            return;
        }
        update({ incomeSource: nextSource, statusOtherExplanation: undefined });
    }

    function handleWorkTypeChange(value: string | boolean | undefined): void {
        const nextStatus = value as EEmploymentStatus;
        // Casual is the only kind that asks for a length — drop it otherwise.
        if (nextStatus !== EEmploymentStatus.CASUAL) {
            update({ status: nextStatus, years: undefined, months: undefined });
            return;
        }
        update({ status: nextStatus });
    }

    // ***** Render *****
    const incomeSourceOptions = [
        {
            label: 'Work',
            value: EIncomeSource.WORK,
            id: `${baseId}-button-incomeSource-work`,
        },
        {
            label: 'Government benefits',
            value: EIncomeSource.GOVERNMENT_PAYMENT,
            id: `${baseId}-button-incomeSource-governmentPayment`,
        },
        {
            label: 'No regular income',
            value: EIncomeSource.NO_INCOME,
            id: `${baseId}-button-incomeSource-noIncome`,
        },
    ];

    const workTypeOptions = [
        {
            label: 'Full-time',
            value: EEmploymentStatus.PERMANENT,
            id: `${baseId}-button-employmentStatus-permanent`,
        },
        {
            label: 'Part-time',
            value: EEmploymentStatus.PART_TIME,
            id: `${baseId}-button-employmentStatus-partTime`,
        },
        {
            label: 'Casual',
            value: EEmploymentStatus.CASUAL,
            id: `${baseId}-button-employmentStatus-casual`,
        },
    ];

    const isWorking = incomeSource === EIncomeSource.WORK;
    const isCasual =
        isWorking && employmentStatus === EEmploymentStatus.CASUAL;

    return (
        <>
            <div data-testid={testId}>
                <RadioGroup
                    title='Where does most of your income come from?'
                    showErrors={showErrors}
                    onChange={handleIncomeSourceChange}
                    options={incomeSourceOptions}
                    value={incomeSource}
                    helperText='If you have more than one, pick the biggest.'
                    tooltipContent='Work is any job you are paid for. Government benefits covers payments such as a pension, allowance or carer payment. Pick "No regular income" if neither applies right now.'
                />
            </div>
            <RevealSlot open={isWorking}>
                {/* Everything that only exists because they said Work sits in
                    one panel, so the follow-ups read as part of that answer
                    rather than as more top-level questions. */}
                <div
                    className={`mt-1 rounded-[10px] px-4 py-3 ${
                        cardGround
                            ? 'border border-cardEdge bg-white'
                            : 'bg-pageGround'
                    }`}
                >
                    <RadioGroup
                        title='What kind of work?'
                        showErrors={showErrors && isWorking}
                        onChange={handleWorkTypeChange}
                        options={workTypeOptions}
                        value={employmentStatus}
                        helperText='Casual usually means no paid annual or sick leave — even if you work full-time hours.'
                        tooltipContent='Full-time and part-time are ongoing roles with paid annual and sick leave. Casual means no guaranteed hours and no paid leave, usually at a higher hourly rate.'
                    />
                    <RevealSlot open={isCasual}>
                        <CasualLength
                            baseId={baseId}
                            showErrors={showErrors && isCasual}
                        />
                    </RevealSlot>
                </div>
            </RevealSlot>
        </>
    );
}
